import React, { useRef, useState } from 'react';
import { Camera, Loader2, Trash2, Upload } from 'lucide-react';
import { api, friendlyError } from '../lib/api';
import { useToast } from '../hooks/use-toast';

const MAX_BYTES = 8 * 1024 * 1024; // 8 MB for avatars — way more than enough
const ACCEPTED = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

// Render either the uploaded avatar or a friendly initials fallback.
// Centralized so it stays consistent everywhere (header, cards, profile, admin).
export function avatarUrl(user, size = 200) {
  if (user?.avatar) return user.avatar;
  const name = (user?.name || user?.email || 'Habita').trim();
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&size=${size}&background=00824D&color=fff&bold=true`;
}

/**
 * AvatarUploader — drag & drop on desktop, tap to pick on mobile.
 * - Local preview BEFORE upload (instant feedback).
 * - Server-side resize + HEIC handling via the existing /api/uploads/image route.
 * - Persists the URL via PATCH /api/auth/me.
 * - "Eliminar" returns the avatar to null (initials fallback).
 */
export default function AvatarUploader({ user, onUpdated }) {
  const { toast } = useToast();
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);   // local object URL
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const currentUrl = preview || user?.avatar || avatarUrl(user, 200);

  const validate = (file) => {
    if (!file) return 'No seleccionaste ninguna imagen.';
    const t = (file.type || '').toLowerCase();
    const okType = ACCEPTED.includes(t) || /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name || '');
    if (!okType) return 'Formato no soportado. Usa JPG, PNG, WEBP o HEIC.';
    if (file.size > MAX_BYTES) {
      return `La imagen pesa ${(file.size / 1024 / 1024).toFixed(1)} MB. Máximo ${MAX_BYTES / 1024 / 1024} MB.`;
    }
    return null;
  };

  const handleFile = async (file) => {
    const err = validate(file);
    if (err) {
      toast({ title: 'No pudimos usar esa imagen', description: err, variant: 'destructive' });
      return;
    }
    // Instant local preview
    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);
    setUploading(true);
    try {
      const res = await api.uploadImage(file);
      const url = res?.url;
      if (!url) throw new Error('Upload sin URL');
      await api.updateMe({ avatar: url });
      toast({ title: 'Foto actualizada', description: 'Tu nueva foto de perfil ya es visible.' });
      if (onUpdated) await onUpdated();
      // Clear local preview so the tile reads the fresh server URL from context.
      setPreview(null);
    } catch (e) {
      setPreview(null);
      toast({
        title: 'No pudimos subir la foto',
        description: friendlyError(e, 'Intenta con una imagen más pequeña o en otro formato.'),
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
      // Revoke after a tick so the new image had time to render
      setTimeout(() => URL.revokeObjectURL(localUrl), 500);
    }
  };

  const removeAvatar = async () => {
    if (!user?.avatar) return;
    setUploading(true);
    try {
      await api.updateMe({ avatar: null });
      setPreview(null);
      toast({ title: 'Foto eliminada' });
      if (onUpdated) await onUpdated();
    } catch (e) {
      toast({ title: 'No pudimos eliminar la foto', description: friendlyError(e), variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  return (
    <div className="flex flex-col sm:flex-row gap-5 sm:gap-7 items-center sm:items-start" data-testid="avatar-uploader">
      {/* Avatar tile — also a drop target on desktop */}
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        role="button"
        tabIndex={0}
        aria-label="Cambiar foto de perfil"
        data-testid="avatar-tile"
        className={`group relative w-28 h-28 sm:w-32 sm:h-32 rounded-full overflow-hidden cursor-pointer ring-4 ring-white shadow-sm transition-all
          ${dragOver ? 'ring-habita scale-[1.02]' : 'hover:ring-habita-mint'}`}
      >
        <img
          src={currentUrl}
          alt={user?.name || 'Avatar'}
          className="w-full h-full object-cover"
          referrerPolicy="no-referrer"
        />
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs font-semibold">
          {uploading
            ? <Loader2 className="w-6 h-6 animate-spin" />
            : <span className="flex items-center gap-1.5"><Camera className="w-4 h-4" /> Cambiar</span>}
        </div>
        {uploading && (
          <div className="absolute inset-0 bg-white/55 flex items-center justify-center">
            <Loader2 className="w-7 h-7 text-habita animate-spin" />
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0 text-center sm:text-left">
        <p className="font-serif text-xl font-bold text-habita-dark">{user?.name}</p>
        <p className="text-xs text-gray-500 mt-0.5 truncate">{user?.email}</p>
        <p className="text-xs text-gray-500 mt-2 max-w-md">
          Sube una foto cuadrada — la optimizamos automáticamente. <span className="hidden sm:inline">Arrastra una imagen aquí o haz clic.</span>
          <span className="block text-[11px] text-gray-400 mt-1">JPG, PNG, WEBP o HEIC · máx. {MAX_BYTES / 1024 / 1024} MB</span>
        </p>
        <div className="flex flex-wrap items-center gap-2 mt-4 justify-center sm:justify-start">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            data-testid="avatar-pick-btn"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-4 h-9 rounded-full bg-habita text-white hover:bg-habita-dark disabled:opacity-60"
          >
            <Upload className="w-3.5 h-3.5" /> {user?.avatar ? 'Cambiar foto' : 'Subir foto'}
          </button>
          {user?.avatar && (
            <button
              type="button"
              onClick={removeAvatar}
              disabled={uploading}
              data-testid="avatar-remove-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-4 h-9 rounded-full border border-gray-200 hover:border-rose-300 hover:text-rose-600 disabled:opacity-60"
            >
              <Trash2 className="w-3.5 h-3.5" /> Eliminar
            </button>
          )}
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
          e.target.value = ''; // allow picking the same file again
        }}
        className="sr-only"
        data-testid="avatar-file-input"
      />
    </div>
  );
}
